/**
 * Single source of truth for "is it worth reissuing this request?" (#3804).
 *
 * Lives apart from {@link ./queryClient} so that callers OUTSIDE react-query can
 * import the policy without pulling in the QueryClient instance. That matters:
 * #3804's acceptance criteria were violated not by the global react-query
 * config but by two hand-rolled poll loops that reimplemented retry locally and
 * classified nothing (`CascadeDeleteProvider`, `useDocumentClassification`).
 * Any new poll loop should call this rather than grow a third policy.
 *
 * Status values come from `HttpError` as normalized by `@aegle/ra-commons`'
 * `parseApiError`, so the full domain is wider than real HTTP codes:
 *   - `0`  — network-level `TypeError` (fetch never got a response)
 *   - `-1` — rejection that wasn't a recognizable API error
 *   - `undefined` — a rejection that skipped the wrapper entirely
 */

/**
 * Marker for a failure raised by client-side pre-flight validation, before any
 * request is issued. Such a rejection is permanent by construction, and it has
 * no HTTP status because it never reached the network.
 *
 * Prefer this over inventing a plausible status (e.g. `new HttpError(…, 400)`):
 * STD-008 §5 prohibits manual `HttpError` construction in data providers, and a
 * synthetic 400 would be categorized as `validation` by `parseApiError` — the
 * same category `withFieldErrors` keys off for form binding — making a guard
 * that never reached a validator indistinguishable from a backend 422.
 */
export class NonRetryableClientError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NonRetryableClientError";
  }
}

/** Transient 4xx statuses. These are the exceptions to "never retry a 4xx". */
export const RATE_LIMIT_STATUS = 429;
export const REQUEST_TIMEOUT_STATUS = 408;

/** Retries allowed for a transient 4xx, deliberately lower than for a 5xx. */
export const TRANSIENT_CLIENT_MAX_RETRIES = 2;

/** Retries allowed for a genuinely transient failure (5xx / network). */
export const TRANSIENT_MAX_RETRIES = 3;

/**
 * Base delay for the ordinary exponential backoff, and the ceiling on any
 * retry delay.
 *
 * These are NOT tuned values — they reproduce react-query's own defaults
 * (`@tanstack/query-core` `retryer.js`: `Math.min(1e3 * 2 ** failureCount, 3e4)`)
 * so that rate limiting is the ONLY status whose backoff deviates from library
 * behavior. Changing either one silently diverges every query in the app from
 * react-query's contract, which is the class of drift this module exists to
 * prevent — so change them only deliberately, and say why here.
 */
export const BASE_DELAY_MS = 1_000;
export const MAX_DELAY_MS = 30_000;

/**
 * Floor for the delay after a 429. The default exponential backoff starts at
 * 1s, but every rate-limit window we emit or expect is an order of magnitude
 * longer (our own `health.go` sets `Retry-After: 15`), so retrying at 1s and 2s
 * would near-certainly 429 again — adding volume at the moment the server asked
 * us to stop. We do not yet read `Retry-After` itself; see the backlog.
 */
export const RATE_LIMIT_BASE_DELAY_MS = 5_000;

export interface RetryClassificationOptions {
  /**
   * Treat 401 as retryable. Correct ONLY for callers that re-acquire a token on
   * every attempt and have no out-of-band recovery of their own.
   *
   * react-query does not need this: a 401 there routes through
   * `authProvider.checkError` → `onSilentRefresh` → `acquireTokenSilent(true)`.
   * The hand-rolled poll loops have no such path, but they DO fetch a fresh
   * token per attempt (`httpClient` → `getAccessToken`), so for them a retry can
   * genuinely succeed. `getAccessToken` returns `null` on a transient MSAL
   * failure and the request is then sent unauthenticated, which manufactures a
   * 401 that one retry usually clears — so without this, a single token blip
   * aborts a cascade delete or reprocess poll on its first tick.
   */
  retryUnauthorized?: boolean;
}

/** Pull the normalized status off an unknown rejection, if it has one. */
export const getErrorStatus = (error: unknown): number | undefined => {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  return typeof status === "number" ? status : undefined;
};

/**
 * True when reissuing the identical request could plausibly succeed.
 *
 * - 4xx → `false`. The input won't change, so a retry cannot help, and each
 *   attempt re-invokes `authProvider.checkError` → one 401/403 toast per
 *   attempt (the "same toast 5 times" symptom #3804 was filed about).
 * - 429 and 408 → `true`. Rate limiting and request timeout are transient by
 *   definition; the ticket's AC names "timeout" as retryable explicitly. This
 *   also matches how our own backend classifies its outbound calls
 *   (`pandadoc/errors.go`, `aura/errors.go`: `429 || >= 500`).
 * - 401 → `false` by default, `true` under `retryUnauthorized`. See
 *   {@link RetryClassificationOptions}.
 * - 5xx and `0` (network) → `true`.
 * - `-1` / `undefined` → `false`. A rejection that carries no status never
 *   reached the server as a recognizable API error — most often a client-side
 *   programming or validation failure, which is permanent.
 */
export const isRetryableStatus = (status: number | undefined, options: RetryClassificationOptions = {}): boolean => {
  if (status === RATE_LIMIT_STATUS || status === REQUEST_TIMEOUT_STATUS) return true;
  if (status === 401) return options.retryUnauthorized === true;
  if (status === undefined || status < 0) return false;
  if (status >= 400 && status < 500) return false;
  return status === 0 || status >= 500;
};

/** `isRetryableStatus` applied straight to a caught rejection. */
export const isRetryableError = (error: unknown, options: RetryClassificationOptions = {}): boolean => {
  if (error instanceof NonRetryableClientError) return false;
  return isRetryableStatus(getErrorStatus(error), options);
};

/**
 * How many retries a given failure is allowed. Callers with their own attempt
 * budget (poll loops) use {@link isRetryableError} plus their own ceiling
 * instead; this exists for the react-query predicate.
 */
export const maxRetriesFor = (status: number | undefined): number =>
  status === RATE_LIMIT_STATUS || status === REQUEST_TIMEOUT_STATUS
    ? TRANSIENT_CLIENT_MAX_RETRIES
    : TRANSIENT_MAX_RETRIES;

/**
 * Delay before the next attempt. Mirrors react-query's exponential default
 * except for rate limiting, which gets a higher floor (see
 * {@link RATE_LIMIT_BASE_DELAY_MS}).
 *
 * 408 deliberately does NOT get that floor, even though it shares the transient
 * carve-out and the budget with 429 in {@link isRetryableStatus} and
 * {@link maxRetriesFor}. The floor exists because a rate limit names a window we
 * must wait out — retrying inside it is guaranteed to fail again. A request
 * timeout carries no such window: the server never asked us to wait, so the
 * ordinary {@link BASE_DELAY_MS} backoff is correct and a 5s floor would only
 * add latency.
 */
export const retryDelayFor = (failureCount: number, status: number | undefined): number => {
  const base = status === RATE_LIMIT_STATUS ? RATE_LIMIT_BASE_DELAY_MS : BASE_DELAY_MS;
  return Math.min(MAX_DELAY_MS, base * 2 ** failureCount);
};
